import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../Assets/Icon/CustomIcon';
import usePhoneCall from '../../../Hooks/useCallHandler';
import EmTelContactCard from '../ui/EmTelContactCard';
import AddContactBottomSheet from '../ui/AddContactBottomSheet';
import RemoveContactBottomSheet from '../ui/RemoveContactBottomSheet';
import { filterEmTelList, groupContactsByChosung } from '../ui/emTelUtils';

export default function M_EMT_BASIC_01({
  emTelList = [],
  depList = [],
  selectedDepCd = '',
  onSelectDepCd,
  fetchDepartmentMembers,
  onAddComplete,
  onRemoveComplete,
  onResumeApprovalChange,
}) {
  const [search, setSearch] = useState('');
  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [isRemoveLoading, setIsRemoveLoading] = useState(false);
  const [departmentMembers, setDepartmentMembers] = useState([]);
  const [isDeptMemberLoading, setIsDeptMemberLoading] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const { handleCall } = usePhoneCall();

  const selectedDep = useMemo(
    () => depList.find((dep) => dep.value === selectedDepCd),
    [depList, selectedDepCd]
  );

  const selectedDepartment = selectedDep?.name ?? '';

  useEffect(() => {
    if (!isAddSheetOpen || !fetchDepartmentMembers) {
      return undefined;
    }

    let cancelled = false;
    setIsDeptMemberLoading(true);

    fetchDepartmentMembers()
      .then((rows) => {
        if (!cancelled) setDepartmentMembers(rows || []);
      })
      .catch(() => {
        if (!cancelled) setDepartmentMembers([]);
      })
      .finally(() => {
        if (!cancelled) setIsDeptMemberLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAddSheetOpen, fetchDepartmentMembers]);

  useEffect(() => {
    if (isAddSheetOpen) return;
    setDepartmentMembers([]);
    setIsDeptMemberLoading(false);
  }, [isAddSheetOpen]);

  const filtered = useMemo(
    () => filterEmTelList(emTelList, { search }),
    [emTelList, search]
  );

  const groupedContacts = useMemo(
    () => groupContactsByChosung(filtered),
    [filtered]
  );

  const handleRemoveConfirm = async () => {
    if (!removeTarget || !onRemoveComplete) return;

    setIsRemoveLoading(true);
    try {
      await onRemoveComplete(removeTarget);
      setRemoveTarget(null);
    } finally {
      setIsRemoveLoading(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#f8faf9] text-[#1b1c1c]">
      {/* 상단: 검색 · 부서 탭 · 안내 (고정) */}
      <div className="mx-auto w-full max-w-2xl shrink-0 space-y-3 px-5 pt-3 pb-2">
        <div className="relative">
          <div className="relative flex items-center rounded-2xl border border-[#dee2e6]/80 bg-white px-5 py-3.5 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_4px_6px_-2px_rgba(0,0,0,0.05)] transition-all focus-within:border-[#34a853] focus-within:ring-4 focus-within:ring-[#34a853]/10">
            <span className="mr-3 text-[#34a853]">
              <Icon.Search width="22" height="22" />
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="부서 내 연락처 검색..."
              className="w-full border-none bg-transparent text-sm text-[#1b1c1c] outline-none placeholder:text-[#5f6368]/50"
            />
          </div>
        </div>

        {depList.length > 0 && (
          <div className="-mx-5 overflow-x-auto px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex min-w-max gap-2">
              {depList.map((dep) => {
                const isActive = selectedDepCd === dep.value;

                return (
                  <button
                    key={dep.value}
                    type="button"
                    onClick={() => onSelectDepCd?.(dep.value)}
                    className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-300 ${
                      isActive
                        ? 'bg-[#34a853] text-white shadow-[0_4px_12px_rgba(52,168,83,0.25)]'
                        : 'border border-[#dee2e6]/50 bg-white text-[#5f6368] hover:bg-[#f1f3f4]'
                    }`}
                  >
                    {dep.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="overflow-hidden rounded-2xl border border-[#cfe8d4] bg-[#f1f8f3]">
          <button
            type="button"
            onClick={() => setIsGuideOpen((prev) => !prev)}
            className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
            aria-expanded={isGuideOpen}
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="shrink-0 text-[#34a853]">
                <Icon.Info width="18" height="18" />
              </span>
              <span className="text-sm font-semibold text-[#1b1c1c]">
                작업재개 승인 권한 안내
              </span>
            </div>
            <span
              className={`shrink-0 text-[#5f6368] transition-transform duration-200 ${
                isGuideOpen ? 'rotate-180' : ''
              }`}
            >
              <Icon.BottomArrow width="20" height="20" />
            </span>
          </button>

          {isGuideOpen && (
            <div className="border-t border-[#cfe8d4] px-4 pt-3 pb-3.5 text-xs leading-relaxed text-[#5f6368]">
              <p>
                비상연락망 담당자 중 작업 중지 건의{' '}
                <span className="font-semibold text-[#1b1c1c]">재개 승인</span>
                을 처리할 수 있는 사람을 지정합니다.
              </p>
              <p className="mt-2 flex items-center gap-2">
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-[#34a853] bg-[#e9f5eb] text-[#34a853]">
                  <Icon.TaskAlt width="15" height="15" />
                </span>
                <span>
                  초록색 아이콘은 권한이 부여된 상태입니다. 버튼을 눌러
                  부여·해제할 수 있습니다.
                </span>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* 하단: 목록만 스크롤 */}
      <div className="mx-auto min-h-0 w-full max-w-2xl flex-1 overflow-y-auto overscroll-contain px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))]">
        <div className="space-y-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold tracking-wide text-[#5f6368] uppercase">
              비상연락망 목록({filtered.length})
            </h2>
            <button
              type="button"
              disabled={!selectedDepCd}
              onClick={() => setIsAddSheetOpen(true)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#dee2e6]/50 px-4 py-2 text-xs font-medium text-[#1b1c1c] transition-colors hover:bg-[#f1f3f4] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Icon.Plus width="18" height="18" />
              연락처 추가
            </button>
          </div>

          {groupedContacts.length > 0 ? (
            <div className="space-y-6">
              {groupedContacts.map(([groupLabel, items]) => (
                <div key={groupLabel} className="space-y-3">
                  <h3 className="px-1 text-sm font-semibold text-[#34a853]">
                    {groupLabel}
                  </h3>
                  {items.map((item) => (
                    <EmTelContactCard
                      key={item._key}
                      item={item}
                      showEdit
                      onEdit={setRemoveTarget}
                      onCall={handleCall}
                      onResumeApprovalChange={onResumeApprovalChange}
                    />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-[#dee2e6]/30 bg-white px-4 py-12 text-center shadow-sm">
              <p className="text-base font-bold text-[#1b1c1c]">
                등록된 비상연락처가 없습니다.
              </p>
            </div>
          )}
        </div>
      </div>

      {isAddSheetOpen && (
        <AddContactBottomSheet
          selectedDepartment={selectedDepartment}
          memberList={departmentMembers}
          existingContacts={emTelList}
          isLoading={isDeptMemberLoading}
          departmentScoped
          onClose={() => setIsAddSheetOpen(false)}
          onComplete={onAddComplete}
        />
      )}

      {removeTarget && (
        <RemoveContactBottomSheet
          contact={removeTarget}
          isLoading={isRemoveLoading}
          onClose={() => {
            if (!isRemoveLoading) setRemoveTarget(null);
          }}
          onConfirm={handleRemoveConfirm}
        />
      )}
    </div>
  );
}
